import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import { dateTimeFormat } from "@/utils";
import { fmtMoney } from "@/utils/money";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

export interface ZoneConsumptionRow {
    /** Vue 接口把合同核心信息放在行级 contract 字段，而不是 consume.contract。 */
    contract?: { id?: string | number };
    consume?: {
        id?: string | number;
        createdTime?: string;
        contractCode?: string;
        reservationOrderId?: string;
        beforeBalanceAmount?: number;
        consumeAmount?: number;
        afterBalanceAmount?: number;
        /** 兼容部分历史转换数据把合同对象嵌套在 consume 下。 */
        contract?: { id?: string | number };
        snapCustomerInfo?: { realName?: string; customerCode?: string; mobile?: string; institutionName?: string };
    };
    specialZoneName?: string;
}

export interface ZoneConsumptionPageResult {
    list?: ZoneConsumptionRow[];
    total?: number;
}

export interface ZoneConsumptionQuery {
    page: number;
    size: number;
    rechargeId?: string;
    keyword?: string;
    contractOrReservation?: string;
    startDate?: string;
    endDate?: string;
    accountId?: string | number;
}

export interface ZoneConsumptionPageProps {
    exportConsumptions?: (query: ZoneConsumptionQuery) => Promise<void>;
    loadConsumptions?: (query: ZoneConsumptionQuery) => Promise<ZoneConsumptionPageResult>;
    permissions?: string[];
    accountId?: string | number;
}

const defaultQuery: ZoneConsumptionQuery = { page: 1, size: 10 };

/** 复用 Vue 专区消费明细分页接口。 */
export async function loadZoneConsumptions(query: ZoneConsumptionQuery): Promise<ZoneConsumptionPageResult> {
    const response = await request.service.get<ZoneConsumptionPageResult>("/admin/special-zone-consume/paging", {
        params: query
    });
    return response.data;
}

/** 从 Vue 导出响应头读取文件名，缺失或编码异常时回退到稳定的中文文件名。 */
function getZoneConsumptionExportFilename(contentDisposition?: string): string {
    const fallback = "专区消费明细.xlsx";
    if (!contentDisposition) return fallback;

    const encodedFilename = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1]?.trim();
    const filename = encodedFilename || contentDisposition.match(/filename=([^;]+)/i)?.[1]?.trim();
    if (!filename) return fallback;

    const normalizedFilename = filename.replace(/^['"]|['"]$/g, "");
    try {
        return decodeURIComponent(normalizedFilename) || fallback;
    } catch {
        return normalizedFilename || fallback;
    }
}

/** 将 Vue 导出接口返回的 Blob 转为浏览器下载，并在点击后清理临时资源。 */
function downloadZoneConsumptionBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue 专区消费明细导出接口，保持筛选参数和原始 Blob 响应契约。 */
export async function exportZoneConsumptions(query: ZoneConsumptionQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/special-zone-consume/export", {
        params: query,
        responseType: "blob",
        returnRawResponse: true
    } as CustomAxiosRequestConfig);
    const filename = getZoneConsumptionExportFilename(response.headers["content-disposition"]);
    downloadZoneConsumptionBlob(response.data, filename);
}

/** 将 Vue 消费记录中的主体快照转换为原页面文案。 */
function getCustomerLabel(row: ZoneConsumptionRow): string {
    const customer = row.consume?.snapCustomerInfo;
    return [customer?.realName, customer?.customerCode].filter(Boolean).join(" | ") || "--";
}

/** 专区消费明细列表，保留 Vue 筛选字段、金额汇总和导出入口。 */
export function ZoneConsumptionPage({
    exportConsumptions = exportZoneConsumptions,
    loadConsumptions = loadZoneConsumptions,
    permissions,
    accountId
}: ZoneConsumptionPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canManage = (permissions ?? storePermissions).includes("ROLE_ZONE_ACCOUNT_MANAGE");
    const [rows, setRows] = useState<ZoneConsumptionRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ZoneConsumptionQuery>(() => ({
        ...defaultQuery,
        ...(accountId == null ? {} : { accountId })
    }));
    const [consumeId, setConsumeId] = useState("");
    const [contractOrReservation, setContractOrReservation] = useState("");
    // 对齐 Vue 用户主体搜索，提交到分页和导出的 keyword 参数。
    const [keyword, setKeyword] = useState("");
    // 对齐 Vue 消费时间筛选，查询接口使用 startDate/endDate。
    const [consumedDateRange, setConsumedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    useEffect(() => {
        let disposed = false;
        void loadConsumptions(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("专区消费明细加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadConsumptions, query]);

    const search = () =>
        setQuery({
            ...defaultQuery,
            ...(accountId == null ? {} : { accountId }),
            ...(consumeId.trim() ? { rechargeId: consumeId.trim() } : {}),
            ...(contractOrReservation.trim() ? { contractOrReservation: contractOrReservation.trim() } : {}),
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(consumedDateRange[0] ? { startDate: consumedDateRange[0] } : {}),
            ...(consumedDateRange[1] ? { endDate: consumedDateRange[1] } : {})
        });

    /** 重置 Vue 消费明细的筛选项，并保留外部账户条件。 */
    const reset = () => {
        setConsumeId("");
        setContractOrReservation("");
        setKeyword("");
        setConsumedDateRange([null, null]);
        setQuery({
            ...defaultQuery,
            ...(accountId == null ? {} : { accountId })
        });
    };

    /** 使用页面已提交的查询条件导出，失败时保留可见的业务反馈。 */
    const handleExport = async () => {
        setExporting(true);
        setErrorMessage(null);
        try {
            await exportConsumptions(query);
        } catch {
            setErrorMessage("专区消费明细导出失败");
        } finally {
            setExporting(false);
        }
    };

    /** 保留 Vue 消费明细合同详情跳转，并在快照缺少合同 ID 时不触发错误导航。 */
    const toContractDetail = (row: ZoneConsumptionRow) => {
        // 专区消费分页响应的合同对象位于行级 contract，兼容历史转换数据中的嵌套字段。
        const contractId = row.contract?.id ?? row.consume?.contract?.id;
        if (contractId == null) return;
        navigate(`/tradeManage/zoneContractDetail?id=${encodeURIComponent(String(contractId))}`);
    };

    /** 保留 Vue 消费明细预约单详情跳转，并使用历史 query.id 传参。 */
    const onShowReservationOrder = (row: ZoneConsumptionRow) => {
        const reservationOrderId = row.consume?.reservationOrderId?.trim();
        if (!reservationOrderId) return;
        navigate(`/tradeManage/zoneReservationOrderDetail?id=${encodeURIComponent(reservationOrderId)}`);
    };

    return (
        <section aria-label="zone-consumption-page">
            <ZhTable
                border
                showSummary
                sumText="合计"
                summaryMethod={({ columns, data }) =>
                    columns.map((column, index) => {
                        if (index === 0) return "合计";
                        if (column.prop === "amount") {
                            return data
                                .reduce((sum, item) => sum + Number(item.consume?.consumeAmount ?? 0), 0)
                                .toFixed(2);
                        }
                        return "";
                    })
                }
                columns={
                    [
                        {
                            label: "消费时间",
                            minWidth: 154,
                            prop: "createdTime",
                            render: ({ row }) =>
                                row.consume?.createdTime
                                    ? dateTimeFormat(row.consume.createdTime, "YYYY-MM-DD HH:mm")
                                    : "--"
                        },
                        {
                            label: "消费编号",
                            minWidth: 176,
                            prop: "consumeId",
                            render: ({ row }) => row.consume?.id || "--"
                        },
                        {
                            label: "关联合同",
                            width: 176,
                            prop: "contract",
                            render: ({ row }) =>
                                row.consume?.contractCode ? (
                                    <Button
                                        className="zone-copyable-link"
                                        type="link"
                                        onClick={() => toContractDetail(row)}
                                    >
                                        {row.consume.contractCode}
                                    </Button>
                                ) : (
                                    "--"
                                )
                        },
                        {
                            label: "关联预约单",
                            minWidth: 176,
                            prop: "reservation",
                            render: ({ row }) =>
                                row.consume?.reservationOrderId ? (
                                    <Button
                                        className="zone-copyable-link"
                                        type="link"
                                        onClick={() => onShowReservationOrder(row)}
                                    >
                                        {row.consume.reservationOrderId}
                                    </Button>
                                ) : (
                                    "--"
                                )
                        },
                        {
                            label: "个人实名/用户ID",
                            minWidth: 204,
                            prop: "customer",
                            render: ({ row }) => getCustomerLabel(row)
                        },
                        {
                            label: "手机号",
                            width: 176,
                            prop: "mobile",
                            render: ({ row }) => row.consume?.snapCustomerInfo?.mobile || "--"
                        },
                        {
                            label: "机构名称",
                            minWidth: 176,
                            prop: "institution",
                            render: ({ row }) => row.consume?.snapCustomerInfo?.institutionName || "--"
                        },
                        {
                            label: "消费专区",
                            minWidth: 176,
                            prop: "zone",
                            render: ({ row }) => row.specialZoneName || "--"
                        },
                        {
                            label: "消费前余额（元）",
                            width: 176,
                            prop: "beforeBalance",
                            render: ({ row }) => fmtMoney(row.consume?.beforeBalanceAmount)
                        },
                        {
                            label: "消费金额（元）",
                            width: 176,
                            prop: "amount",
                            render: ({ row }) => fmtMoney(row.consume?.consumeAmount)
                        },
                        {
                            label: "消费后余额（元）",
                            width: 176,
                            prop: "afterBalance",
                            render: ({ row }) => fmtMoney(row.consume?.afterBalanceAmount)
                        }
                    ] as TableColumnProps<ZoneConsumptionRow>[]
                }
                data={rows}
                pagination={false}
                rowKey={row => String(row.consume?.id)}
                onReset={reset}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}

                        <Space className="admin-table-filter-form mb-4" wrap>
                            {canManage ? (
                                <Button
                                    aria-label="导出消费"
                                    type="primary"
                                    loading={exporting}
                                    onClick={() => void handleExport()}
                                >
                                    导出
                                </Button>
                            ) : null}
                            <Input
                                aria-label="消费编号"
                                allowClear
                                className="zone-consumption-id"
                                placeholder="请输入消费编号搜索"
                                value={consumeId}
                                onChange={event => setConsumeId(event.target.value)}
                            />
                            <Input
                                aria-label="用户ID/实名/手机号"
                                allowClear
                                className="zone-consumption-keyword"
                                placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                value={keyword}
                                onChange={event => setKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="合同或预约单"
                                allowClear
                                className="zone-consumption-contract"
                                placeholder="请输入关联合同/预约单号搜索"
                                value={contractOrReservation}
                                onChange={event => setContractOrReservation(event.target.value)}
                            />
                            <label>
                                消费日期
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="消费开始日期"
                                    endPlaceholder="消费结束日期"
                                    value={consumedDateRange}
                                    onChange={(_value, formattedValue) =>
                                        setConsumedDateRange(
                                            formattedValue
                                                ? [formattedValue[0] || null, formattedValue[1] || null]
                                                : [null, null]
                                        )
                                    }
                                />
                            </label>
                            <Button
                                aria-label="查询消费"
                                icon={<TableSearchIcon name="search" />}
                                type="primary"
                                onClick={search}
                            >
                                搜索
                            </Button>
                        </Space>
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            className="mt-4 self-end"
                            current={query.page}
                            pageSize={query.size}
                            total={total}
                            onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                        />
                    </div>
                }
            />
        </section>
    );
}

export default ZoneConsumptionPage;
